import { Component, OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { NgForm } from '@angular/forms'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { UntypedFormBuilder, FormControl, FormGroup } from '@angular/forms'
import { Subscription } from 'rxjs'

import { SiteService } from '../../../../services/site.service'
import { MessageService } from '../../../../services/message.service'
import { TranslateService } from '@ngx-translate/core'
import { CustomerRequestsService } from '../../../../services/customer-requests.service'

import { PaymentDateChangeViewModel } from '../../../../models/paymentDateChangeViewModel'
import { ErrorMessageModalComponent } from '../../../modals/error-message-modal/error-message-modal.component'
import { PaymentFrequencyTypes } from '../../../../enumeration'

@Component({
    selector: 'app-request-payment-date-change-detail',
    templateUrl: './request-payment-date-change-detail.component.html',
    styleUrls: ['./request-payment-date-change-detail.component.scss'],
    standalone: false
})
export class RequestPaymentDateChangeDetailComponent implements OnInit 
{
	@ViewChild('customForm') public customForm!: NgForm
	formSubmitted: boolean = false
	showLoanMore: boolean = false
	showPaymentDayOfWeek: boolean = false
	isActiveLease: boolean = true

	validNewPaymentDateSelection: { display: string, value: Date }[] = []
	newPaymentDate: Date | undefined = undefined
	pageIndex: number = 0
	pageSize: number = 5
	referenceNumber: string = ''
	paymentDateChangeViewModel = new PaymentDateChangeViewModel()
	paymentDayOfWeek: string = ''

	preferredLanguageChangedSubscription: Subscription

	constructor(
		private router: Router,
		public dialog: MatDialog,
		private siteService: SiteService,
		private messageService: MessageService,
		private translateService: TranslateService,
		private formBuilder: UntypedFormBuilder,
		private customerRequestsService: CustomerRequestsService
	) 
	{ 
		this.preferredLanguageChangedSubscription = this.messageService.preferredLanguageChanged$.subscribe(() =>
		{
			this.reset()
			this.ngOnInit()
		})
	}

	ngOnInit() 
	{
		this.retrievePaymentDateChangeViewModel()
	}

	reset()
	{
		this.showLoanMore = false
		this.showPaymentDayOfWeek = false
		this.isActiveLease = true
		this.validNewPaymentDateSelection = []
		this.newPaymentDate = undefined
		this.pageIndex = 0
		this.pageSize = 5
		this.referenceNumber = ''
		this.paymentDateChangeViewModel = new PaymentDateChangeViewModel()
		this.paymentDayOfWeek = ''
	}

	retrievePaymentDateChangeViewModel()
	{
		this.messageService.startedHttpRequest('retrievePaymentDateChangeViewModel')
		this.customerRequestsService
			.getPaymentDateChangeViewModel(this.pageIndex, this.pageSize)
			.subscribe({
				next: (apiResponse) =>
				{
					this.paymentDateChangeViewModel = apiResponse

					if(this.paymentDateChangeViewModel && this.paymentDateChangeViewModel.isActiveLease)
					{
						this.paymentDateChangeViewModel.validNewPaymentDates.forEach(validNewPaymentDate =>
						{
							const dateObj = new Date(validNewPaymentDate)
							const locale = this.translateService.currentLang === 'fr' ? 'fr-FR' : 'en-US'

							const  validDate = dateObj.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})

							this.validNewPaymentDateSelection.push({ display: validDate, value: dateObj })
						})
						this.showPaymentDayOfWeek = (this.paymentDateChangeViewModel.paymentFrequencyTypeId == PaymentFrequencyTypes.BiWeekly) || (this.paymentDateChangeViewModel.paymentFrequencyTypeId == PaymentFrequencyTypes.Weekly)
						this.showLoanMore = this.paymentDateChangeViewModel.totalRecords > this.validNewPaymentDateSelection.length	
						
						if(this.showPaymentDayOfWeek)
						{
							this.paymentDayOfWeek = this.translateService.currentLang === 'fr' ? this.siteService.daysFR[this.paymentDateChangeViewModel.paymentDayOfWeek] ?? this.paymentDateChangeViewModel.paymentDayOfWeek : this.paymentDateChangeViewModel.paymentDayOfWeek
						}
					}
					else
					{
						this.isActiveLease = false
					}
				},
				error: (error) =>
				{
					this.isActiveLease = false
					this.messageService.completedHttpRequest('retrievePaymentDateChangeViewModel')
			
					const dialogConfig = new MatDialogConfig()
					dialogConfig.width = '500px',
					dialogConfig.data = {
						title: this.translateService.instant('COMMON_LBL_ERROR'),
						message: this.translateService.instant('COMMON_LBL_ERROR_MSG')
					}		
					this.dialog.open(ErrorMessageModalComponent, dialogConfig)
				},
				complete: () =>
				{
					this.messageService.completedHttpRequest('retrievePaymentDateChangeViewModel')
				}
			})
	}

	handleLoadMore()
	{
		this.pageIndex = this.pageIndex + 1
		this.retrievePaymentDateChangeViewModel()
	}

	submitForm(customerForm: NgForm)
	{
		this.formSubmitted = true

		if (customerForm.invalid || !this.newPaymentDate)
		{
			return
		}

		const requestSummaryList: { key: string; value: string }[] = [
			{ key: this.translateService.instant('REQUEST_PAYMENT_DUE_DATE_CHANGE_CONFIRM_NAME'), value: this.siteService.customerAccount?.fullName ?? ''},
			{ key: this.translateService.instant('REQUEST_PAYMENT_DUE_DATE_CHANGE_CONFIRM_LEASENUM'), value: this.paymentDateChangeViewModel.leaseNumber }
		]

		const selectedPaymentDate = new Date(this.newPaymentDate)
		switch(this.paymentDateChangeViewModel.paymentFrequencyTypeId)
		{
			case PaymentFrequencyTypes.Weekly:
			case PaymentFrequencyTypes.BiWeekly:
				const locale = this.translateService.currentLang === 'fr' ? 'fr-FR' : 'en-US'
				requestSummaryList.push({ key: this.translateService.instant('REQUEST_PAYMENT_DUE_DATE_CHANGE_CONFIRM_PAYMENT_DAY'), value: selectedPaymentDate.toLocaleDateString(locale, { weekday: 'long' })})
				break
			case PaymentFrequencyTypes.Monthly:
				requestSummaryList.push({ key: this.translateService.instant('REQUEST_PAYMENT_DUE_DATE_CHANGE_CONFIRM_PAYMENT_DAY'), value: selectedPaymentDate.getFullYear() + '/' + (selectedPaymentDate.getMonth() + 1) + '/' + selectedPaymentDate.getDate()})
				break
		}
		requestSummaryList.push({ key: this.translateService.instant('REQUEST_PAYMENT_DUE_DATE_CHANGE_CONFIRM_EFFECTIVE_DATE'), value: selectedPaymentDate.getFullYear() + '/' + (selectedPaymentDate.getMonth() + 1) + '/' + selectedPaymentDate.getDate()})

		return this.customerRequestsService.requestConfirmation(this.translateService.instant('REQUEST_PAYMENT_DUE_DATE_CHANGE_CONFIRM_PRINT_TITLE'), requestSummaryList)
		.then((result) =>
		{
			if(result)
			{
				let changeRequest =
				{
					newPaymentDate: this.newPaymentDate
				}

				this.messageService.startedHttpRequest('submitForm')
				this.customerRequestsService
				.addPaymentDateChangeRequest(changeRequest)
				.subscribe({
					next: (apiResponse) =>
					{
						this.referenceNumber = apiResponse

						if ((this.referenceNumber != undefined) && (this.referenceNumber != ''))
						{
							this.customerRequestsService.requestSummary(this.referenceNumber, this.translateService.instant('REQUEST_PAYMENT_DUE_DATE_CHANGE_HEADING'), requestSummaryList)
							
							this.router.navigate(['/request-list'])							
						}	
													
						this.formSubmitted = false
					},
					error: (error) =>
					{
						this.messageService.completedHttpRequest('submitForm')
			
						const dialogConfig = new MatDialogConfig()
						dialogConfig.width = '500px',
						dialogConfig.data = {
							title: this.translateService.instant('COMMON_LBL_ERROR'),
							message: this.translateService.instant('COMMON_LBL_ERROR_MSG')
						}		
						this.dialog.open(ErrorMessageModalComponent, dialogConfig)
					},
					complete: () =>
					{
						this.messageService.completedHttpRequest('submitForm')
					}
				})
			}
		})	
	}
}
